import { type SiteId } from '@solinkcorp/api-types';
import {
  Button,
  Calendar,
  cn,
  LocationsFilterButton,
  LocationsFilterIconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useSidebar,
} from '@solinkcorp/solink-ui';
import { isToday as checkIsToday } from '@solinkcorp/utils';
import {
  type DayOfWeekNumber,
  HISTOGRAM_RANGE,
  HistogramApiHelper,
  type HistogramRange,
} from '@solinkcorp/vd-common';
import { decrementDate, incrementDate, setFilterDate, setRange } from 'actions/filterActions';
import { CommonContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import { areCapsulesFilteredByDateTimeField } from 'components/EventFilter/utils';
import { shouldCommitDraftRange } from 'components/NavigationFrame/navigation/dateFilterPolicy';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import { CalendarIcon, Check, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import AnalyticsLogger from 'models/AnalyticsLogger';
import moment from 'moment';
import { type FC, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useControllerState } from 'solox';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import CameraStore from 'stores/CameraStore';
import EventFilterStore from 'stores/EventFilterStore';
import { useDispatch, useSelector } from 'stores/store';
import { useEventStandard } from 'utils/EventStandardCache/eventStandardCache';
import FeaturesUtil from 'utils/FeaturesUtil';
import { type CameraGroupWithCameras } from 'utils/types';
import {
  calendarDay,
  clampInstant,
  datePickerLocale,
  formatClockTime,
  formatPickerLabel,
  formatTimezone,
  instantOnCalendarDay,
  labelWithoutWeekday,
  leftCalendarMonth,
  resolveTimezone,
  selectableInstantBounds,
} from './datePickerTime';
import { PeriodJump, type PickerView } from './PeriodJump';
import { TimeSheet } from './TimeSheet';

const footerChipClass =
  'flex min-w-0 items-center gap-1.5 rounded-md bg-transparent py-1 pl-1.5 pr-2.5 text-foreground-secondary body-sm outline-hidden transition-colors hover:bg-interaction-secondary-background-hover hover:text-foreground-primary focus-visible:ring-2 focus-visible:ring-sidebar-ring';

export interface GlobalDatePickerRangeOption {
  label: number | string;
  value: HistogramRange;
}

interface GlobalDatePickerProps {
  /** Sidebar layout: full-width button when expanded, calendar icon on the collapsed rail. */
  variant: 'expanded' | 'collapsed';
  /** Called to toggle off-live. */
  handleToggleClick?: () => void;
  /** False if the live toggle is currently on. */
  isOnInToggle?: boolean;
  minDate: Date | null | undefined;
  maxDate?: Date | null | undefined;
  /** Called when the selected day or range unit changes. */
  onChangeRangeDate?: () => void;
  /** Called whenever the user applies a selection, even if nothing changed. */
  onDatePicked?: () => void;
  selectedLocationIds: readonly SiteId[];
  showLiveToggle?: boolean;
  /** Turns playback to live. Omitted when live is not available on this page. */
  onGoLive?: () => void;
  showTimeInput?: boolean;
  disabled?: boolean;
  /** Blocks date, time, and range changes. The popover still opens so Go Live stays reachable. */
  lockDateSelection?: boolean;
  /**
   * Range units the page supports. Without them the picker selects a single day and
   * ignores `filter.range`, because the page does not read it.
   */
  rangeOptions?: readonly GlobalDatePickerRangeOption[];
  /** True when the range unit means "the last N ending now" rather than a calendar window. */
  rangeRolling?: boolean;
  /** True when prev and next step by the selected range. False steps one day. */
  navigateByRange?: boolean;
}

const GlobalDatePicker: FC<GlobalDatePickerProps> = (props) => {
  const {
    variant,
    handleToggleClick,
    isOnInToggle,
    selectedLocationIds,
    showLiveToggle,
    onGoLive,
    showTimeInput,
    rangeOptions,
    rangeRolling = false,
    lockDateSelection = false,
    navigateByRange = false,
  } = props;

  const dispatch = useDispatch();
  const locale = useLocale();
  const { firstDayOfWeek } = useContext(CommonContext).authData;

  const {
    date,
    range,
    cameraGroupId,
    timezoneId: timezoneIdOverride,
  } = useSelector((state) => state.filter);

  const playingCamerasController = usePlayingCamerasContext();
  const playingCameraIds = useControllerState(
    playingCamerasController.state,
    (state) => state.playingCameraIds,
  );

  const eventStandard = useEventStandard();
  const fieldStats = useSelector((state) => state.fieldStats.fieldStats);
  const capsules = useFluxStore(EventFilterStore, (store) => store.getAppState().capsules);
  const cameraGroups: CameraGroupWithCameras[] = useFluxStore(CameraStore, (store) =>
    store.getCameraGroups(),
  );

  const disabledByFilters =
    eventStandard && fieldStats
      ? areCapsulesFilteredByDateTimeField(eventStandard, fieldStats, capsules)
      : capsules.length;
  const disabled = props.disabled || !!disabledByFilters;

  const selectedGroup = cameraGroups?.find((group) => group.id === cameraGroupId);
  const timezone = resolveTimezone({
    playingLocationId: playingCameraIds?.[0]?.locationId,
    groupLocationId: selectedGroup?.cameras[0]?.siteId,
    selectedLocationIds,
    timezoneIdOverride,
  });

  const minDate = props.minDate ?? undefined;
  const maxDate = props.maxDate ?? undefined;
  const hasRangeOptions = !!rangeOptions && rangeOptions.length > 0;
  const rangeIsSupported = !!rangeOptions?.some((option) => option.value === range);
  const unit: HistogramRange = rangeIsSupported ? range : HISTOGRAM_RANGE.DAY;

  useEffect(() => {
    if (hasRangeOptions && !rangeIsSupported) {
      dispatch(setRange(HISTOGRAM_RANGE.DAY));
    }
  }, [dispatch, hasRangeOptions, rangeIsSupported]);

  const [open, setOpen] = useState(false);
  const [draftUnit, setDraftUnit] = useState<HistogramRange>(unit);
  const [draftDate, setDraftDate] = useState<number>(date);
  // Live commits with Apply and drops with Cancel, same as the date.
  const [draftLive, setDraftLive] = useState(false);
  // Instant shown before Live was checked. Unchecking restores it.
  const dateBeforeLive = useRef<number | null>(null);
  // Playback keeps moving `date` while the popover is open. Apply must not write this snapshot back.
  const dateAtOpen = useRef(date);
  const [month, setMonth] = useState<Date>(() => calendarDay(date, timezone));
  const [viewMode, setViewMode] = useState<PickerView>('day');
  const [sheet, setSheet] = useState<'date' | 'time'>('date');
  const [browseYear, setBrowseYear] = useState(() => calendarDay(date, timezone).getFullYear());

  const windowFor = (anchor: number, windowUnit: HistogramRange) =>
    HistogramApiHelper.getDateRangeInfo({
      date: rangeRolling && windowUnit !== HISTOGRAM_RANGE.DAY ? Date.now() : anchor,
      range: windowUnit,
      rangeRolling: rangeRolling && windowUnit !== HISTOGRAM_RANGE.DAY,
      shortString: true,
      timezone,
      firstDayOfWeek: firstDayOfWeek as DayOfWeekNumber | undefined,
      locale,
    });

  const committedWindow = windowFor(date, unit);
  const draftWindow = windowFor(draftDate, draftUnit);
  const today = calendarDay(Date.now(), timezone);
  const lastDay =
    maxDate && calendarDay(maxDate, timezone) < today ? calendarDay(maxDate, timezone) : today;
  const maxYear = lastDay.getFullYear();
  const earliestDay = minDate ? calendarDay(minDate, timezone) : undefined;
  const minYear = Math.min(maxYear, earliestDay ? earliestDay.getFullYear() : maxYear - 100);
  const liveIsOn = !!showLiveToggle && isOnInToggle === false;
  // Live playback does not write `date`, so the closed label keeps its own clock.
  const [liveNow, setLiveNow] = useState(() => Date.now());
  useEffect(() => {
    if (!liveIsOn) {
      return;
    }
    setLiveNow(Date.now());
    const timer = window.setInterval(() => setLiveNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, [liveIsOn]);

  useEffect(() => {
    if (!open || !draftLive) {
      return;
    }
    const timer = window.setInterval(() => {
      setDraftDate((current) => {
        const now = Date.now();
        return moment(current).isSame(now, 'minute') ? current : now;
      });
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [open, draftLive]);

  // Both layouts stay mounted. Only the visible one may own the shared test id and Intercom target.
  // Content portals to document.body, so a hidden trigger would leave a floating calendar.
  const { state: sidebarState } = useSidebar();
  const triggerVisible =
    variant === 'collapsed' ? sidebarState === 'collapsed' : sidebarState !== 'collapsed';
  useEffect(() => {
    if (!triggerVisible) {
      setOpen(false);
    }
  }, [triggerVisible]);

  const focusDraft = (instant: number) => {
    setDraftDate(instant);
    setMonth(leftCalendarMonth(calendarDay(instant, timezone), earliestDay, true));
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraftUnit(unit);
      dateAtOpen.current = date;
      setDraftLive(liveIsOn);
      setViewMode('day');
      setSheet('date');
      if (liveIsOn) {
        dateBeforeLive.current = date;
        focusDraft(Date.now());
      } else {
        dateBeforeLive.current = null;
        focusDraft(date);
      }
      AnalyticsLogger.openDatePicker({ isOpen: true });
    }
    setOpen(next);
  };

  const toggleDraftLive = () => {
    setViewMode('day');
    setSheet('date');
    if (draftLive) {
      const previous = dateBeforeLive.current;
      dateBeforeLive.current = null;
      setDraftLive(false);
      if (previous != null) {
        focusDraft(previous);
      }
      return;
    }
    dateBeforeLive.current = draftDate;
    setDraftLive(true);
    focusDraft(Date.now());
  };

  const leaveLive = () => {
    setDraftLive(false);
  };

  const draftBounds = () =>
    selectableInstantBounds(minDate?.valueOf(), maxDate?.valueOf(), Date.now());

  // setFilterDate replaces a future instant with now. Keep the draft inside the clock
  // so Apply stores the time the sheet shows.
  useEffect(() => {
    if (!open || draftLive || lockDateSelection) {
      return;
    }
    const { latest } = selectableInstantBounds(undefined, maxDate?.valueOf(), Date.now());
    if (latest !== undefined && draftDate > latest) {
      setDraftDate(latest);
    }
  }, [open, draftLive, lockDateSelection, draftDate, maxDate]);

  const handleDayClick = (day: Date) => {
    if (lockDateSelection) {
      return;
    }
    leaveLive();
    setDraftDate(
      clampInstant(
        instantOnCalendarDay(day, timezone, showTimeInput ? draftDate : undefined).valueOf(),
        draftBounds(),
      ),
    );
    // A rolling range ignores the picked day, so the click selects Day before Apply.
    if (rangeRolling) {
      setDraftUnit(HISTOGRAM_RANGE.DAY);
    }
  };

  const liveChanged = draftLive !== liveIsOn;

  const handleApply = () => {
    if (lockDateSelection && !liveChanged) {
      return;
    }

    if (liveChanged) {
      if (draftLive) {
        onGoLive?.();
      } else {
        handleToggleClick?.();
      }
    }

    // Going live replaces the playhead. A locked picker can still commit that switch.
    if (lockDateSelection || draftLive) {
      setOpen(false);
      return;
    }

    const appliedInstant = clampInstant(draftDate, draftBounds());
    const instantChanged = appliedInstant !== dateAtOpen.current;
    if (!instantChanged && draftUnit === unit) {
      setOpen(false);
      return;
    }

    const dateToChange = new Date(appliedInstant);

    const dayChanged =
      instantChanged && !moment.tz(dateToChange, timezone).isSame(dateAtOpen.current, 'day');
    if (dayChanged || draftUnit !== unit) {
      props.onChangeRangeDate?.();
    }

    // onDatePicked already switches a changed day to Day. Write the draft unit
    // unless that switch is still the selection, so a later Week choice is kept.
    props.onDatePicked?.();
    if (instantChanged) {
      dispatch(setFilterDate(dateToChange.valueOf()));
      AnalyticsLogger.selectDateViaDatePicker({ date: dateToChange });
    }
    if (
      shouldCommitDraftRange({
        hasRangeOptions,
        dayPickSwitchesToDay: !!props.onDatePicked,
        dayChanged,
        draftUnitIsDay: draftUnit === HISTOGRAM_RANGE.DAY,
      })
    ) {
      dispatch(setRange(draftUnit));
    }
    setOpen(false);
  };

  const stepUnit = navigateByRange ? unit : HISTOGRAM_RANGE.DAY;
  // Live steps from now. The stored date can still sit on the earliest day.
  const stepFrom = liveIsOn ? liveNow : date;
  const atMinDay = !!minDate && moment(stepFrom).isSameOrBefore(minDate, 'day');
  const atToday = liveIsOn || checkIsToday(date, timezone);
  const stepsDisabled = disabled || lockDateSelection;

  const stepDay = (direction: -1 | 1) => {
    if (stepsDisabled) return;
    if (direction < 0 && atMinDay) return;
    if (direction > 0 && atToday) return;
    props.onDatePicked?.();
    props.onChangeRangeDate?.();
    const stepAnchor = liveIsOn ? Date.now() : undefined;
    if (direction < 0) {
      dispatch(decrementDate({ unit: stepUnit, minDate, date: stepAnchor }));
    } else {
      dispatch(incrementDate({ unit: stepUnit, date: stepAnchor }));
    }
    if (liveIsOn) {
      handleToggleClick?.();
    }
    setOpen(false);
  };

  const labelInstant = liveIsOn ? liveNow : date;
  const labelWindow = liveIsOn ? windowFor(liveNow, HISTOGRAM_RANGE.DAY) : committedWindow;
  // Clock time applies to one day. A week, month, or year label stays on one line without it.
  const labelShowsTime = !!showTimeInput && (liveIsOn || unit === HISTOGRAM_RANGE.DAY);
  const draftShowsTime = !!showTimeInput && draftUnit === HISTOGRAM_RANGE.DAY;
  const closedLabel = formatPickerLabel(
    labelWithoutWeekday(labelWindow.formatted),
    labelInstant,
    timezone,
    labelShowsTime,
  );

  const triggerProps = {
    disabled,
    id: triggerVisible ? 'global-date-picker' : undefined,
    'data-intercom-target': triggerVisible ? 'global-date-picker' : undefined,
    'data-testid': triggerVisible ? 'date-picker-text' : undefined,
    'aria-label': closedLabel,
  };

  const trigger =
    variant === 'collapsed' ? (
      <LocationsFilterIconButton {...triggerProps} isActive={open}>
        <CalendarIcon aria-hidden />
      </LocationsFilterIconButton>
    ) : (
      <LocationsFilterButton
        {...triggerProps}
        icon={<CalendarIcon aria-hidden />}
        // Shell draws the border. Override `truncate` so the date, time, and zone stay visible.
        className="h-auto min-h-9 rounded-none border-0 bg-transparent px-1 py-2 shadow-none hover:bg-transparent [&>span]:overflow-visible [&>span]:whitespace-normal [&>span]:text-center"
      >
        {closedLabel}
      </LocationsFilterButton>
    );

  const dayStep = (direction: -1 | 1) => (
    <button
      type="button"
      className="flex shrink-0 items-center justify-center px-1.5 text-interaction-neutral-content-default disabled:pointer-events-none disabled:opacity-40"
      aria-label={direction < 0 ? locale.general.back : locale.general.next}
      data-testid={direction < 0 ? 'date-picker-prev-day' : 'date-picker-next-day'}
      disabled={stepsDisabled || (direction < 0 ? !!atMinDay : atToday)}
      onClick={(event) => {
        event.stopPropagation();
        stepDay(direction);
      }}
    >
      {direction < 0 ? (
        <ChevronLeft aria-hidden className="size-3.5" />
      ) : (
        <ChevronRight aria-hidden className="size-3.5" />
      )}
    </button>
  );

  const withDaySteps = (control: ReactNode) =>
    variant === 'collapsed' ? (
      control
    ) : (
      <div className="flex w-full min-w-0 items-center rounded-lg border border-border-muted bg-background-surface">
        {dayStep(-1)}
        <div className="min-w-0 flex-1">{control}</div>
        {dayStep(1)}
      </div>
    );

  const restrictedMessage =
    liveIsOn && props.disabled
      ? FeaturesUtil.isLiveViewOnly()
        ? locale.playerMessage.liveViewOnlyRestricted
        : locale.playerMessage.controlRestricted
      : undefined;

  if (restrictedMessage) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex w-full" data-testid="date-picker-container">
            {withDaySteps(trigger)}
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">{restrictedMessage}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {variant === 'collapsed' ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            {closedLabel}
          </TooltipContent>
        </Tooltip>
      ) : (
        withDaySteps(<PopoverTrigger asChild>{trigger}</PopoverTrigger>)
      )}
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={8}
        data-testid="date-picker-container"
        className="tw flex w-auto overflow-hidden rounded-xl border border-border p-0 shadow-xl"
      >
        {hasRangeOptions && (
          <div
            className={cn(
              'flex w-32 shrink-0 flex-col gap-1 border-r border-border px-2 py-2',
              lockDateSelection && 'pointer-events-none opacity-40',
            )}
          >
            {rangeOptions.map((option) => {
              const selected = draftUnit === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={lockDateSelection}
                  className={cn(
                    'flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm px-3 py-1.5 text-left text-foreground-primary body-sm',
                    'outline-hidden transition-colors focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                    'disabled:pointer-events-none',
                    selected ? 'bg-background-elevated' : 'hover:bg-background-elevated/60',
                  )}
                  onClick={() => {
                    if (lockDateSelection) {
                      return;
                    }
                    leaveLive();
                    setDraftUnit(option.value);
                    if (option.value !== HISTOGRAM_RANGE.DAY) {
                      setSheet('date');
                    }
                  }}
                  data-testid={`date-picker-range-${option.value}`}
                >
                  <span className="whitespace-nowrap">{option.label}</span>
                  <Check
                    aria-hidden
                    className={cn(
                      'size-4 shrink-0',
                      selected ? 'text-foreground-accent' : 'invisible',
                    )}
                  />
                </button>
              );
            })}
          </div>
        )}

        <div className="flex min-w-[34rem] flex-col px-6 pb-4 pt-5">
          <div className={cn(lockDateSelection && 'pointer-events-none opacity-40')}>
            {draftShowsTime && sheet === 'time' ? (
              <TimeSheet
                value={draftDate}
                timeZone={timezone}
                bounds={draftBounds()}
                labels={{
                  hour: locale.date.hour,
                  minute: locale.general.mins,
                  am: locale.fields.am,
                  pm: locale.fields.pm,
                }}
                onChange={(next) => {
                  if (lockDateSelection) {
                    return;
                  }
                  const clamped = clampInstant(next, draftBounds());
                  // A repeat tap zeroes seconds, so compare the clock the sheet shows.
                  if (moment.tz(clamped, timezone).isSame(draftDate, 'minute')) {
                    return;
                  }
                  leaveLive();
                  setDraftDate(clamped);
                }}
              />
            ) : viewMode === 'day' ? (
              <Calendar
                mode="range"
                numberOfMonths={2}
                pagedNavigation
                showOutsideDays={false}
                // Months span 4–6 week rows. Always render 6 so paging does not resize the popover.
                fixedWeeks
                month={month}
                onMonthChange={setMonth}
                // Keeps the newest selectable month in the right-hand slot instead of an all-disabled future month.
                endMonth={lastDay}
                startMonth={earliestDay}
                selected={{
                  from: calendarDay(draftWindow.start, timezone),
                  to: calendarDay(draftWindow.end, timezone),
                }}
                onSelect={(_range, day) => handleDayClick(day)}
                disabled={[{ after: lastDay }, ...(earliestDay ? [{ before: earliestDay }] : [])]}
                weekStartsOn={firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined}
                locale={datePickerLocale(locale.localeCode)}
                // The nav bar is stretched across both captions, so without this the title click hits the bar.
                // `\_` keeps the underscore in rdp class names; a bare `_` is a space in Tailwind selectors.
                className="tw mx-auto p-0 [&_.rdp-button\_next]:pointer-events-auto [&_.rdp-button\_previous]:pointer-events-auto [&_.rdp-nav]:pointer-events-none"
                // The Calendar root sets `--cell-size` inline, so a class cannot override it.
                style={{ ['--cell-size' as string]: '2.25rem' }}
                classNames={{
                  months: 'rdp-months relative flex flex-col gap-4 md:flex-row md:gap-10',
                }}
                components={{
                  MonthCaption: ({
                    calendarMonth,
                    displayIndex: _displayIndex,
                    children,
                    ...captionProps
                  }) => (
                    <div {...captionProps}>
                      <button
                        type="button"
                        className="relative z-10 cursor-pointer rounded-md px-2 py-1 font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                        onClick={() => {
                          setBrowseYear(calendarMonth.date.getFullYear());
                          setViewMode('month');
                        }}
                      >
                        {children}
                      </button>
                    </div>
                  ),
                }}
              />
            ) : (
              <PeriodJump
                viewMode={viewMode}
                browseYear={browseYear}
                minYear={minYear}
                maxYear={maxYear}
                minDay={earliestDay}
                maxDay={lastDay}
                selectedMonth={calendarDay(draftDate, timezone)}
                localeCode={locale.localeCode}
                onBrowseYear={setBrowseYear}
                onShowMonths={() => setViewMode('month')}
                onShowYears={() => setViewMode('year')}
                onPickMonth={(monthIndex) => {
                  const picked = new Date(browseYear, monthIndex, 1);
                  const lastMonth = new Date(lastDay.getFullYear(), lastDay.getMonth(), 1);
                  const onRight = picked.getTime() >= lastMonth.getTime();
                  setMonth(leftCalendarMonth(onRight ? lastMonth : picked, earliestDay, onRight));
                  setViewMode('day');
                }}
              />
            )}
          </div>

          {lockDateSelection && (
            <p className="mt-4 text-foreground-secondary body-sm">
              {locale.playerMessage.controlRestricted}
            </p>
          )}

          <div className="-mx-6 mt-5 flex items-center justify-between gap-6 border-t border-border pl-4 pr-6 pt-4">
            {draftShowsTime ? (
              <div className="flex min-w-0 items-center gap-1">
                {onGoLive && (
                  <button
                    type="button"
                    className={cn(
                      footerChipClass,
                      'justify-center px-2.5',
                      draftLive &&
                        'bg-primary font-medium text-primary-foreground hover:bg-primary',
                    )}
                    aria-pressed={draftLive}
                    onClick={toggleDraftLive}
                    data-testid="date-picker-go-live"
                  >
                    {locale.general.live}
                  </button>
                )}
                {sheet === 'time' ? (
                  <button
                    type="button"
                    className={cn(
                      footerChipClass,
                      'disabled:pointer-events-none disabled:opacity-40',
                    )}
                    disabled={lockDateSelection}
                    onClick={() => {
                      setViewMode('day');
                      setSheet('date');
                    }}
                    data-testid="date-picker-show-date"
                  >
                    <CalendarIcon className="size-4 shrink-0" aria-hidden />
                    <span className="truncate">{draftWindow.formatted}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className={cn(
                      footerChipClass,
                      'disabled:pointer-events-none disabled:opacity-40',
                    )}
                    disabled={lockDateSelection}
                    onClick={() => setSheet('time')}
                    data-testid="date-picker-show-time"
                  >
                    <Clock className="size-4 shrink-0" aria-hidden />
                    {formatClockTime(draftDate, timezone)} {formatTimezone(draftDate, timezone)}
                  </button>
                )}
              </div>
            ) : (
              <span className="text-foreground-secondary body-sm">
                {labelWithoutWeekday(draftWindow.formatted)}
              </span>
            )}
            <div className="flex gap-3">
              <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
                {locale.general.cancel}
              </Button>
              <Button
                variant="default"
                size="sm"
                disabled={lockDateSelection && !liveChanged}
                onClick={handleApply}
              >
                {locale.general.apply}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default GlobalDatePicker;
